/**
 * @module api/episodes
 *
 * API functions for managing episodes: CRUD operations, playlist management,
 * starring/unstarring, moving between podcasts, cache management, and
 * download reset. All functions communicate with the server via the
 * shared Axios instance from config.js.
 */

import { api } from "./config.js";

// NOTE: a video_id goes into the URL PATH of several endpoints below, and it
// is NOT guaranteed to be URL-safe. Most are clean YouTube/Rumble ids, but the
// id is derived from the source URL, and a malformed "video" (e.g. an X/Twitter
// image URL pasted into Add URL) can produce one containing "?" and "&" —
// seen in production as video_id "HQnxIHsbkAA879p?format=jpg&name=small".
// Interpolated raw, the "?" starts a QUERY STRING: the server's :video_id then
// matched only the part before it, so deleting that episode failed with "not
// found" and the row could not be removed from the UI at all. Every path
// interpolation of an id here is therefore encodeURIComponent'd.

/**
 * Fetch a single global count: how many episodes visible to this user
 * (across all their podcasts, Latest, and Playlist) are currently
 * downloading or uploading. Backs the AppBar's persistent activity
 * indicator — polled independently of which page is open (unlike the
 * per-list /status endpoints, which only run their own status polls while
 * that specific view is mounted), so App.vue polls this one globally.
 *
 * @returns {Promise<{success: boolean, data: {count: number}}>}
 * @throws {Error} If the request fails, with server message or fallback
 */
const getDownloadingCount = async () => {
  try {
    const response = await api.get("/episodes/downloading-count");
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get downloading count",
    );
  }
};

/**
 * Delete a specific episode's cached media file from the server.
 * This removes the downloaded video/audio file but keeps the episode
 * metadata in the database.
 *
 * @param {string} video_id - The unique video identifier of the episode
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If deletion fails, with server message or fallback
 */
const deleteFromCache = async (video_id) => {
  try {
    const response = await api.delete(`/admin/delete-from-cache/${encodeURIComponent(video_id)}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to delete file from cache for episode ID: ${video_id}`,
    );
  }
};

/**
 * Submit a URL (YouTube channel, playlist, or single video) to the server
 * for processing. The server will extract metadata and create episodes
 * from the provided URL.
 *
 * @param {string} url - The URL to process (YouTube, Rumble, RaiPlaySound, etc.)
 * @returns {Promise<Object>} Server response data including job information
 * @throws {Error} If the request fails, with server message or fallback
 */
const addUrl = async (url) => {
  let data;
  try {
    const response = await api.post("/add-url", { url });
    data = response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
  // /add-url risponde 200 ANCHE quando fallisce: il suo catch imposta
  // success:false nel corpo e lascia lo stato a 200 "to avoid triggering
  // client-side error handlers". Senza guardare il corpo l'errore sparisce —
  // esattamente il difetto già corretto per triggerCache. Qui lo si trasforma
  // in un'eccezione, così il chiamante non deve ricordarsene.
  if (data && data.success === false) {
    throw new Error(data.error?.message || data.error || "Failed to get episodes");
  }
  return data;
};

/**
 * Move multiple episodes to a specific podcast by creating associations
 * in the podcast_episodes junction table.
 *
 * @param {number} podcast_id - Target podcast ID to move episodes into
 * @param {number[]} episode_ids - Array of episode IDs to move
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the move fails, with server message or fallback
 */
const moveEpisodes = async (podcast_id, episode_ids) => {
  try {
    const response = await api.post("/episodes/podcast", {
      podcast_id,
      episode_ids,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to move selected episodes to podcast`,
    );
  }
};

/**
 * Move a single episode from the "latest" list to a specific podcast.
 * This removes the episode from the latest list and associates it
 * with the target podcast.
 *
 * @param {number} podcast_id - Target podcast ID
 * @param {number} episode_id - Episode ID to move from latest
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the move fails, with server message or fallback
 */
const moveEpisodeFromLatest = async (podcast_id, episode_id) => {
  try {
    const response = await api.post("/latest/move-episode", {
      podcast_id,
      episode_id,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to move episode from latest to podcast`,
    );
  }
};

/**
 * Move multiple episodes from the "latest" list to a specific podcast —
 * bulk version of moveEpisodeFromLatest, for the Latest view's checkbox
 * multi-select "Move Episodes To Podcast" action.
 *
 * @param {number} podcast_id - Target podcast ID
 * @param {number[]} episode_ids - Episode IDs to move from latest
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the move fails, with server message or fallback
 */
const moveEpisodesFromLatest = async (podcast_id, episode_ids) => {
  try {
    const response = await api.post("/latest/move-episodes", {
      podcast_id,
      episode_ids,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to move episodes from latest to podcast`,
    );
  }
};

/**
 * Move a single episode to a specific podcast by creating an association
 * in the podcast_episodes junction table.
 *
 * @param {number} podcast_id - Target podcast ID
 * @param {number} episode_id - Episode ID to associate with the podcast
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the move fails, with server message or fallback
 */
const moveEpisode = async (podcast_id, episode_id) => {
  try {
    const response = await api.post("/episode/podcast", {
      podcast_id,
      episode_id,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to move episode to podcast`,
    );
  }
};

/**
 * Star (bookmark/favorite) an episode by its ID.
 *
 * @param {number} id - The episode ID to star
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the star operation fails
 */
const starEpisode = async (id) => {
  try {
    const response = await api.post(`/episode/star/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
};

/**
 * Remove the star (bookmark/favorite) from an episode by its ID.
 *
 * @param {number} id - The episode ID to unstar
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the unstar operation fails
 */
const unstarEpisode = async (id) => {
  try {
    const response = await api.post(`/episode/unstar/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
};

/**
 * Delete multiple episodes from the database by their IDs.
 * This permanently removes both metadata and any cached media files
 * due to CASCADE delete configuration on junction tables.
 *
 * @param {number[]} episode_ids - Array of episode IDs to delete
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the deletion fails
 */
const deleteEpisodes = async (episode_ids) => {
  try {
    const response = await api.delete(`/episodes`, { data: { episode_ids } });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to delete episodes`,
    );
  }
};

/**
 * Delete a single episode from the database by its ID.
 * Requires a confirmation payload ("DELETE") as a safety measure
 * to prevent accidental deletions.
 *
 * @param {number} episodeId - The episode ID to delete
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the deletion fails
 */
const deleteEpisode = async (episodeId) => {
  try {
    const response = await api.delete(`/episode/${encodeURIComponent(episodeId)}`, { data: { confirmation: "DELETE" } });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to delete episode: ${episodeId}`,
    );
  }
};

/**
 * Retrieve all episodes from the server (not scoped to a specific podcast).
 *
 * @returns {Promise<Object>} Server response data containing the episodes array
 * @throws {Error} If the fetch fails
 */
const getEpisodes = async () => {
  try {
    const response = await api.get("/episodes");
    // console.log(response.data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get episodes`);
  }
};

// export const streamEpisode = async (url) => {
//   try {
//     const response = await api.post("/episodes", {
//       url,
//     });
//     return response;
//   } catch (error) {
//     throw new Error(error.response?.data?.message || "Failed to stream");
//   }
// };

/**
 * Add an episode to the user's playlist.
 * The episode is added at position 0 (top) and existing positions shift down.
 *
 * @param {Object} episode - The episode object to add
 * @param {number} episode.id - The episode's database ID
 * @param {string} episode.title - The episode title (used in error messages)
 * @throws {Error} If the server returns an error or the request fails
 */
const addToPlaylist = async (episode) => {
  try {
    const { data } = await api.put("/playlist/episode/" + episode.id);
    // Check the response body for application-level errors
    if (!data.success) {
      throw new Error(data.error.message);
    }
  } catch (error) {
    // Preserve the HTTP status code so callers can distinguish
    // 409 (duplicate) from other errors and show appropriate feedback
    const status = error.response?.status;
    const message =
      error.response?.data?.message ||
      `Episode "${episode.title}" not added to the playlist`;
    const err = new Error(message);
    err.status = status;
    throw err;
  }
};

/**
 * Start (or check on) server-side caching of an episode without waiting for
 * it to finish — the fire-and-forget trigger used by every "add to
 * playlist" quick action and swipe gesture across the app.
 *
 * Normalizes the TWO different shapes a failure can take into one thing
 * every caller can react to identically:
 *   - a THROWN HTTP error (4xx/5xx from the server, or a network failure)
 *   - a 200 OK response body with `{ success: false, giveUp: true, message }`
 *     — controllers/video.js's maxDownloadFailures gate declining to even
 *     attempt a download for an episode that's already failed too many
 *     times in a row. This is NOT an HTTP error status, so plain
 *     `.catch()` handling never sees it — every one of this app's ~8
 *     call sites had this exact gap until 2026-08-31: the request
 *     "succeeded" as far as axios was concerned, the response body was
 *     never inspected, and the episode silently stayed uncached forever
 *     with zero feedback. Confirmed live: episode 262742 had hit the
 *     failure cap, and tapping Cache produced no error, no snackbar, and
 *     no console output at all — it just looked like nothing happened.
 *
 * Always resolves (never rejects) — callers that want to react to a
 * failure check the returned `{ ok, message }` rather than try/catch,
 * since a rejected promise here would just recreate the exact
 * silently-swallowed-by-fire-and-forget-callers problem this exists to fix.
 *
 * @param {Object} episode
 * @param {string} episode.video_id
 * @param {string} [episode.episode_type] - "audio" -> .mp3, anything else -> .mp4
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
const triggerCache = async (episode) => {
  const ext = episode.episode_type === "audio" ? "mp3" : "mp4";
  try {
    const { data } = await api.get(`/play/${encodeURIComponent(episode.video_id)}.${ext}`, {
      params: { cache: true },
    });
    if (data?.success === false) {
      return { ok: false, message: data.message || "Could not start caching this episode" };
    }
    return { ok: true };
  } catch (error) {
    if (error.code === "ECONNABORTED") return { ok: true }; // client-side timeout on a fire-and-forget call — not a real failure signal
    return {
      ok: false,
      message:
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Could not start caching "${episode.title || episode.video_id}"`,
    };
  }
};

/**
 * Reset the download state for a specific episode.
 * This clears cached_at, progress, and progress_message fields,
 * allowing the episode to be re-downloaded from scratch.
 *
 * @param {string} video_id - The unique video identifier of the episode
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the reset fails
 */
/**
 * "Check IMDb" (context menu → More actions): ask the server to search IMDb
 * for the episode's title. Resolves to the response `data`:
 *   { matched: true, url, title, year, type, query }   — a title page, also
 *                                                        saved on the episode
 *   { matched: false, url, query }                     — IMDb's search page
 *                                                        for the title, nothing saved
 * The caller opens `url` in a new tab either way.
 *
 * @param {number|string} episodeId - Episode primary key
 * @returns {Promise<Object>}
 */
/**
 * Stored IMDb match + cached film details for the "Check IMDb" dialog:
 *   null                          — never matched (caller runs checkImdb)
 *   { url, info: Object|null }    — info = plot/director/cast… (null when
 *                                    the details couldn't be fetched)
 * @param {number|string} episodeId
 * @returns {Promise<null|{url: string, info: Object|null}>}
 */
const getImdbInfo = async (episodeId) => {
  try {
    const response = await api.get(`/episode/imdb/${encodeURIComponent(episodeId)}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(error.response?.data?.message || "IMDb details not retrieved");
  }
};

const checkImdb = async (episodeId) => {
  try {
    const response = await api.post(`/episode/imdb/${encodeURIComponent(episodeId)}`);
    return response.data?.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "IMDb lookup failed");
  }
};

const resetDownload = async (video_id) => {
  try {
    const response = await api.post(`/episode/reset-download/${encodeURIComponent(video_id)}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to reset download for episode: ${video_id}`,
    );
  }
};

/**
 * Fetch the full metadata set for the episode INFO dialog: timestamps
 * (uploaded / added / cached / saved / starred), duration, source, storage,
 * and the file size — the REAL byte size read from storage when the episode is
 * cached, otherwise yt-dlp's estimate (`size_is_exact` says which).
 *
 * @param {number} id - The episode's database id
 * @returns {Promise<Object>} Episode details object
 * @throws {Error} If the fetch fails
 */
const getEpisodeDetails = async (id) => {
  try {
    const response = await api.get(`/episode/details/${id}`);
    return response.data.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to load episode details",
    );
  }
};

/**
 * Stop an in-flight download for a specific episode.
 *
 * Unlike resetDownload (which only clears the database fields and lets the
 * running yt-dlp finish and overwrite them), this actually kills the running
 * download — locally or on the proxy — or aborts the job if it's still queued.
 * The episode is left in a pristine, never-downloaded state.
 *
 * @param {string} video_id - The unique video identifier of the episode
 * @returns {Promise<Object>} Server response { success, state, message }
 * @throws {Error} If the request fails
 */
const cancelDownload = async (video_id) => {
  try {
    const response = await api.post(`/episode/cancel-download/${encodeURIComponent(video_id)}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        `Failed to stop download for episode: ${video_id}`,
    );
  }
};

/**
 * Save an episode by its ID, protecting it from cache cleanup deletion.
 * Saved episodes will not be removed by the scheduled cache cleanup routine,
 * keeping their cached media files available indefinitely.
 *
 * @param {number} id - The episode ID to save
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the save operation fails
 */
const saveEpisode = async (id) => {
  try {
    const response = await api.post(`/episode/save/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to save episode`);
  }
};

/**
 * Remove the saved flag from an episode, allowing normal cache cleanup.
 * The episode's cached file will become eligible for deletion when it expires.
 *
 * @param {number} id - The episode ID to unsave
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the unsave operation fails
 */
const unsaveEpisode = async (id) => {
  try {
    const response = await api.post(`/episode/unsave/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to unsave episode`);
  }
};

/**
 * Update editable metadata fields of an episode (title and/or notes).
 * Only the fields provided in the data object are sent to the server.
 *
 * @param {number} id - The episode's database ID
 * @param {Object} data - Fields to update
 * @param {string} [data.title] - New episode title
 * @param {string|null} [data.notes] - User notes for this episode (null to clear)
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the update fails
 */
const updateEpisode = async (id, data) => {
  try {
    const response = await api.put(`/episode/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to update episode ${id}`);
  }
};

/**
 * Add/replace an episode's thumbnail — either an uploaded image file or an
 * image URL (exactly one).
 *
 * @param {number} id - The episode's database ID
 * @param {Object} params
 * @param {File} [params.file] - An image file to upload
 * @param {string} [params.url] - An image URL to fetch instead
 * @returns {Promise<Object>} Server response data ({ thumbnail_path })
 * @throws {Error} If neither/both are given, or the update fails
 */
const updateEpisodeThumbnail = async (id, { file, url } = {}) => {
  const formData = new FormData();
  if (file) formData.append("thumbnail", file);
  if (url) formData.append("thumbnailUrl", url);

  try {
    const response = await api.put(`/episode/${id}/thumbnail`, formData, {
      // Same reasoning as api/sideload.js: the shared instance's default
      // Content-Type: application/json must be explicitly cleared for a
      // FormData body, or it silently goes out as a broken JSON request
      // instead of real multipart — axios does not auto-override an
      // INHERITED default header just because the body is FormData.
      headers: { "Content-Type": undefined },
    });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to update thumbnail for episode ${id}`);
  }
};

// ─── Play history ────────────────────────────────────────────────────────────

/**
 * Record (or refresh) a play of an episode in the user's play history.
 * Fire-and-forget from the player; failures must never interrupt playback, so
 * this swallows errors. Only real integer episode ids are sent (standalone
 * synthetic ids are skipped by the caller).
 *
 * @param {number} episodeId - The episode's database ID
 * @returns {Promise<void>}
 */
const recordPlay = async (episodeId) => {
  try {
    await api.post(`/history/${episodeId}`);
  } catch {
    /* non-fatal: history is best-effort */
  }
};

/**
 * Add listened (content) time to an episode's running total in play history.
 * Fire-and-forget; errors are swallowed so playback is never affected.
 *
 * @param {number} episodeId - The episode's database ID
 * @param {number} seconds - Whole seconds of listening to add (>0)
 * @returns {Promise<void>}
 */
const recordListened = async (episodeId, seconds) => {
  try {
    await api.post(`/history/${episodeId}/listened`, { seconds });
  } catch {
    /* non-fatal: listened-time is best-effort */
  }
};

/**
 * Fetch the user's play history (most recently played first).
 * @returns {Promise<Object>} Server response data ({ success, history })
 */
const getHistory = async () => {
  const response = await api.get("/history");
  return response.data;
};

/**
 * Remove a single episode from the user's play history.
 * @param {number} episodeId - The episode's database ID
 * @returns {Promise<Object>} Server response data
 */
const removeFromHistory = async (episodeId) => {
  const response = await api.delete(`/history/${episodeId}`);
  return response.data;
};

/**
 * Clear the user's entire play history.
 * @returns {Promise<Object>} Server response data
 */
const clearHistory = async () => {
  const response = await api.delete("/history");
  return response.data;
};

/**
 * Exported API object containing all episode-related functions.
 * Consumed by the barrel export in api/index.js and by composables.
 */
export const episodesApi = {
  addToPlaylist,
  triggerCache,
  getDownloadingCount,
  deleteFromCache,
  addUrl,
  getEpisodes,
  deleteEpisode,
  deleteEpisodes,
  // streamEpisode,
  moveEpisode,
  moveEpisodes,
  starEpisode,
  unstarEpisode,
  // ottobre 10, 2025
  moveEpisodeFromLatest,
  moveEpisodesFromLatest,
  resetDownload,
  checkImdb,
  getImdbInfo,
  cancelDownload,
  getEpisodeDetails,
  saveEpisode,
  unsaveEpisode,
  updateEpisode,
  updateEpisodeThumbnail,
  recordPlay,
  recordListened,
  getHistory,
  removeFromHistory,
  clearHistory,
};
